import * as PopoverPrimitve from '@radix-ui/react-popover'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useNavigate } from 'react-router-dom'
import { EmptyState } from '../../../features/empty-state'
import { useNotifications } from '../../../features/notifications'
import { useAuth } from '../../hooks/useAuth'
import { styled } from '../../lib/stitches'
import { secondsToMilliseconds } from '../../utils/time'
import CenteredLoadingAnimation from '../core/CenteredLoadingAnimation'
import { Bell } from '../core/icons'
import Text from '../core/Text/Text'
import Notification from '../profile/Notification'

const segmentButtonClick = (button: string) => {
  window.analytics.track('Button Clicked', {
    button: button,
    location: window.location.pathname
  })
}
const NotificationContent = styled(PopoverPrimitve.Content, {
  display: 'flex',
  flexDirection: 'column',
  background: '$gray01',
  boxShadow: '0px 4px 40px rgba(52, 78, 92, 0.15)',
  borderRadius: '10px',
  padding: '16px',
  zIndex: 10,
  maxHeight: '75vh',
  boxSizing: 'border-box',
  width: '420px'
})

const NotificationList = styled('div', {
  padding: '8px 0',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'scroll',
  gap: '21px',
  '-ms-overflow-style': 'none',
  'scrollbar-width': 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  }
})

const NotificationArrow = styled(PopoverPrimitve.Arrow, {
  fill: '$gray01'
})

const NotificationRoot = PopoverPrimitve.Root
const NotificationPortal = PopoverPrimitve.Portal

const NotificationTrigger = styled(PopoverPrimitve.Trigger, {
  all: 'unset',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '32px',
  height: '32px',
  borderRadius: 4,
  boxSizing: 'border-box',
  cursor: 'pointer',
  marginRight: '8px',
  color: '$whiteA500'
})

const NotificationBadge = styled('div', {
  position: 'absolute',
  top: '-7px',
  right: '-7px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '29px',
  height: '20px',
  borderRadius: '50px',
  backgroundColor: '#E84AD8',
  fontSize: '14px',
  letterSpacing: '-0.02em',
  color: '$gray01'
})

const PinkBell = styled(Bell, {
  path: {
    stroke: '#E84AD8',
    fill: '#E84AD8'
  }
})

function Notifications() {
  const [open, setOpen] = useState<boolean>(false)
  const [unreadNotificationsCounter, setUnreadNotificationsCounter] = useState(0)
  const { user } = useAuth()
  const navigate = useNavigate()
  const { data, isLoading, fetchNextPage, hasNextPage, refetch } = useNotifications({
    userId: user?.id,
    params: {
      perPage: 50,
      order: 'desc',
      orderBy: 'createdAt'
    },
    options: {
      refetchInterval: secondsToMilliseconds(30),
      enabled: !!user
    }
  })

  const flattenData = data?.pages?.flatMap((page) => page.data)

  useEffect(() => {
    const notificationsCount =
      flattenData?.reduce((sum, next) => {
        if (next.readAt === null) {
          sum++
        }
        return sum
      }, 0) % 100
    setUnreadNotificationsCounter(notificationsCount)
  }, [data])

  if (!data) {
    return null
  }

  const onOpenChange = (value: boolean) => {
    open
      ? segmentButtonClick('Notification Bell (close)')
      : segmentButtonClick('Notification Bell (open)')
    if (!value && unreadNotificationsCounter > 0) {
      setUnreadNotificationsCounter(0)
      // todo find better way, issue is that refetch is called before unread from Notification child component
      setTimeout(refetch, 500)
    }

    setOpen(value)
  }

  return (
    <NotificationRoot open={open} onOpenChange={onOpenChange}>
      <NotificationTrigger>
        {open ? <PinkBell /> : <Bell />}
        {!open && unreadNotificationsCounter > 0 && (
          <NotificationBadge>{unreadNotificationsCounter}</NotificationBadge>
        )}
      </NotificationTrigger>
      <NotificationPortal>
        <NotificationContent side="bottom" collisionPadding={8} sideOffset={4}>
          {isLoading ? (
            <CenteredLoadingAnimation />
          ) : (
            <>
              <Text
                size="20"
                fontWeight="medium"
                css={{
                  lineHeight: '20px',
                  color: '$gray15',
                  paddingBottom: '15px',
                  letterSpacing: '-0.02em'
                }}
              >
                Notifications
              </Text>
              {data.pages[0].meta.total === 0 && (
                <EmptyState.Root>
                  <EmptyState.Icon />
                  <EmptyState.Text>No notifications yet!</EmptyState.Text>
                  <EmptyState.ButtonRow>
                    <EmptyState.PrimaryButton onClick={() => navigate('/studio')}>
                      Start creating
                    </EmptyState.PrimaryButton>
                  </EmptyState.ButtonRow>
                </EmptyState.Root>
              )}
              <NotificationList id="scrollableNotification">
                <InfiniteScroll
                  loader={<CenteredLoadingAnimation />}
                  dataLength={flattenData.length}
                  next={fetchNextPage}
                  hasMore={hasNextPage}
                  scrollableTarget="scrollableNotification"
                  style={{ overflow: 'hidden' }}
                >
                  {data.pages.map((page) =>
                    page.data.map(({ id, readAt, createdAt, data }) => (
                      <Notification
                        key={id}
                        id={id}
                        readAt={readAt}
                        createdAt={createdAt}
                        type={data.type}
                        arpId={data.arpId}
                        data={data}
                      />
                    ))
                  )}
                </InfiniteScroll>
              </NotificationList>
            </>
          )}

          <NotificationArrow />
        </NotificationContent>
      </NotificationPortal>
    </NotificationRoot>
  )
}

export default observer(Notifications)
